import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    MdMovie,
    MdDownload,
    MdPictureInPicture,
    MdFullscreen,
    MdFullscreenExit,
    MdSpeed,
    MdReplay5,
    MdForward5,
    MdErrorOutline,
    MdCheck,
    MdClose
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { getAegisAiDocumentMedia, getAegisAiDocumentById } from '../../Services/AegisAiDocumentsService';
import { downloadProviderFile } from '../../Services/ProviderService';
import '../../Styles/Modal.css';

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export default function AiVideoPlayerModal({
    isOpen,
    onClose,
    title,
    fileName,
    fileSize,
    videoUrl,
    documentId,
    fileId,
    posterUrl,
    onDownload
}) {
    const { token } = useAuth();
    const videoRef = useRef(null);
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);

    // Active playable URL: prioritize explicitly passed videoUrl (e.g. Azure Blob SAS URL), fallback to blobUrl
    const activeUrl = videoUrl || blobUrl;

    useEffect(() => {
        if (!isOpen) {
            setLoading(false);
            setError('');
            return;
        }

        // If direct stream URL already provided (e.g. from Azure Blob SAS URL), use immediately
        if (videoUrl) {
            setBlobUrl(null);
            setLoading(false);
            setError('');
            return;
        }

        let isMounted = true;
        const abortController = new AbortController();

        const loadVideo = async () => {
            setLoading(true);
            setError('');
            try {
                // First attempt: fetch document details to check for fast streaming SAS URL
                if (documentId) {
                    try {
                        const docRes = await getAegisAiDocumentById(documentId, token);
                        if (!isMounted) return;
                        if (docRes?.is_success && docRes?.data?.MediaStreamUrl) {
                            setBlobUrl(docRes.data.MediaStreamUrl);
                            setLoading(false);
                            return;
                        }
                    } catch (_) {}
                }

                // Fallback: download media blob
                let blob = null;
                if (documentId) {
                    blob = await getAegisAiDocumentMedia(documentId, 'media', token, abortController.signal);
                } else if (fileId) {
                    const res = await downloadProviderFile(token, fileId);
                    blob = res?.blob;
                }

                if (!isMounted) return;

                if (blob) {
                    const url = URL.createObjectURL(blob);
                    setBlobUrl(url);
                    setLoading(false);
                } else {
                    setError('Video file could not be loaded from storage.');
                    setLoading(false);
                }
            } catch (err) {
                if (err.name === 'AbortError') return;
                console.error('Error loading video:', err);
                if (isMounted) {
                    setError(err?.message || 'Failed to stream video. Please try again.');
                    setLoading(false);
                }
            }
        };

        loadVideo();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [isOpen, videoUrl, documentId, fileId, token]);

    // Clean up created blob URL when unmounting
    useEffect(() => {
        return () => {
            if (blobUrl && blobUrl.startsWith('blob:')) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [blobUrl]);

    // Handle speed change
    const handleSpeedChange = (rate) => {
        setPlaybackRate(rate);
        if (videoRef.current) {
            videoRef.current.playbackRate = rate;
        }
    };

    // Seek +/- seconds
    const handleSeek = (delta) => {
        if (!videoRef.current) return;
        videoRef.current.currentTime = Math.max(0, Math.min(videoRef.current.duration || 0, videoRef.current.currentTime + delta));
    };

    // Picture in picture toggle
    const handlePip = async () => {
        if (!videoRef.current) return;
        try {
            if (document.pictureInPictureElement) {
                await document.exitPictureInPicture();
            } else if (videoRef.current.requestPictureInPicture) {
                await videoRef.current.requestPictureInPicture();
            }
        } catch (e) {
            console.warn('Picture-in-picture error:', e);
        }
    };

    // Fullscreen toggle
    const handleFullscreen = () => {
        if (!videoRef.current) return;
        if (!document.fullscreenElement) {
            videoRef.current.requestFullscreen().catch(() => {});
            setIsFullscreen(true);
        } else {
            document.exitFullscreen().catch(() => {});
            setIsFullscreen(false);
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Handle video download
    const handleDownload = async () => {
        if (onDownload) {
            onDownload();
            return;
        }

        setIsDownloading(true);
        try {
            let downloadName = fileName || `${title || 'video'}.mp4`;

            if (activeUrl) {
                const response = await fetch(activeUrl);
                const downloadBlob = await response.blob();
                const url = URL.createObjectURL(downloadBlob);
                const a = document.createElement('a');
                a.href = url;
                a.download = downloadName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 2000);
                setDownloaded(true);
                setTimeout(() => setDownloaded(false), 2500);
            } else if (fileId) {
                const res = await downloadProviderFile(token, fileId);
                if (res?.blob) {
                    const url = URL.createObjectURL(res.blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = res.filename || downloadName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 2000);
                    setDownloaded(true);
                    setTimeout(() => setDownloaded(false), 2500);
                }
            }
        } catch (err) {
            console.error('Download error:', err);
            toast.error('Failed to download video');
        } finally {
            setIsDownloading(false);
        }
    };

    // Keyboard shortcuts (Escape, Space)
    const handleKeyDown = useCallback((e) => {
        if (!isOpen) return;
        const target = e.target;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
            return;
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        } else if (e.key === ' ' || e.key === 'k') {
            e.preventDefault();
            if (videoRef.current) {
                if (videoRef.current.paused) videoRef.current.play();
                else videoRef.current.pause();
            }
        } else if (e.key === 'f' || e.key === 'F') {
            e.preventDefault();
            handleFullscreen();
        }
    }, [isOpen, onClose]);

    // Standard body scroll freeze while modal is open, restored on close
    useEffect(() => {
        if (!isOpen) return;
        window.addEventListener('keydown', handleKeyDown);
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    const displayTitle = title || fileName || 'Video Player';

    // Portalled directly to <body> for a true, clean, full-screen modal popup
    return createPortal(
        <div
            className="modal d-block hc-modal-backdrop hc-ai-video-modal-backdrop"
            tabIndex="-1"
            role="presentation"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="modal-dialog modal-dialog-centered modal-xl hc-ai-video-modal-dialog">
                <div
                    className="modal-content hc-modal-content hc-ai-video-modal-content border-0"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Preview video: ${displayTitle}`}
                >
                    {/* Header */}
                    <div className="hc-modal-header d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3 overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                            <span className="hc-modal-icon flex-shrink-0">
                                <MdMovie size={22} />
                            </span>
                            <div className="hc-modal-header-text overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                                <h5 className="modal-title fw-bold mb-0 text-truncate" title={displayTitle}>
                                    {displayTitle}
                                </h5>
                                <div className="text-muted small d-flex align-items-center gap-2 mt-1">
                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                                        MP4 Video
                                    </span>
                                    {fileSize ? <span>• {fileSize}</span> : null}
                                    {fileName && fileName !== displayTitle ? (
                                        <span className="text-truncate opacity-75" title={fileName}>• {fileName}</span>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 fw-semibold"
                                onClick={handleDownload}
                                disabled={isDownloading}
                                title="Download video file"
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        <span>Downloading...</span>
                                    </>
                                ) : downloaded ? (
                                    <>
                                        <MdCheck size={16} color="#10b981" />
                                        <span style={{ color: '#10b981' }}>Saved</span>
                                    </>
                                ) : (
                                    <>
                                        <MdDownload size={16} />
                                        <span>Download</span>
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                className="hc-modal-close"
                                onClick={onClose}
                                aria-label="Close"
                                title="Close (Esc)"
                            >
                                &times;
                            </button>
                        </div>
                    </div>

                    {/* Modal Body / Video Player */}
                    <div className="p-3 bg-light">
                        <div className="hc-ai-video-player-frame position-relative">
                            {loading ? (
                                <div
                                    className="d-flex flex-column align-items-center justify-content-center w-100 h-100 p-4"
                                    style={posterUrl ? { backgroundImage: `linear-gradient(rgba(10,15,29,0.85), rgba(10,15,29,0.95)), url(${posterUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                                >
                                    <div className="spinner-border text-primary mb-3" style={{ width: '2.5rem', height: '2.5rem' }} role="status">
                                        <span className="visually-hidden">Loading video...</span>
                                    </div>
                                    <div className="fw-semibold text-white fs-6 mb-1">Connecting to Secure Storage</div>
                                    <div className="text-white-50 small">Streaming video...</div>
                                </div>
                            ) : error ? (
                                <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100 p-4 text-center">
                                    <MdErrorOutline size={44} className="text-danger mb-2" />
                                    <div className="fw-bold text-white fs-6 mb-1">Unable to Load Video</div>
                                    <div className="text-white-50 small mb-3" style={{ maxWidth: 420 }}>{error}</div>
                                    <button
                                        type="button"
                                        className="btn btn-outline-light btn-sm"
                                        onClick={handleDownload}
                                    >
                                        <MdDownload size={15} className="me-1" />
                                        Download Video File
                                    </button>
                                </div>
                            ) : activeUrl ? (
                                <video
                                    ref={videoRef}
                                    src={activeUrl}
                                    poster={posterUrl || undefined}
                                    controls
                                    autoPlay
                                    playsInline
                                    className="w-100 h-100"
                                />
                            ) : null}
                        </div>
                    </div>

                    {/* Modal Footer Controls */}
                    <div className="hc-modal-footer d-flex align-items-center justify-content-between p-3">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {/* Speed Selector */}
                            <div className="d-inline-flex align-items-center gap-1 bg-white border rounded-2 px-2 py-1">
                                <span className="small text-muted d-inline-flex align-items-center gap-1 me-1">
                                    <MdSpeed size={14} />
                                    <span>Speed:</span>
                                </span>
                                {SPEED_OPTIONS.map((rate) => (
                                    <button
                                        key={rate}
                                        type="button"
                                        className={`btn btn-sm py-0 px-2 fw-semibold ${playbackRate === rate ? 'btn-primary' : 'btn-light text-muted'}`}
                                        style={{ fontSize: '11.5px' }}
                                        onClick={() => handleSpeedChange(rate)}
                                    >
                                        {rate}x
                                    </button>
                                ))}
                            </div>

                            {/* Seek Controls */}
                            <div className="btn-group btn-group-sm">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => handleSeek(-5)}
                                    title="Rewind 5s (←)"
                                >
                                    <MdReplay5 size={16} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => handleSeek(5)}
                                    title="Forward 5s (→)"
                                >
                                    <MdForward5 size={16} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handlePip}
                                    title="Picture in Picture"
                                >
                                    <MdPictureInPicture size={16} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleFullscreen}
                                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                >
                                    {isFullscreen ? <MdFullscreenExit size={16} /> : <MdFullscreen size={16} />}
                                </button>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <span className="small text-muted d-none d-md-inline">
                                Space: Play/Pause • Esc: Close
                            </span>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm px-3"
                                onClick={onClose}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
